<template>
    <div class="dispatch-summary">
        <div class="content-block dx-card responsive-paddings">
            <h3>Review &amp; Save</h3>
            <p class="muted">Confirm the dispatch details below, then save and share.</p>

            <div class="info-grid">
                <div class="info-item">
                    <span class="label">Name</span>
                    <span class="value">{{ dispatch.name || '(unnamed)' }}</span>
                </div>
                <div class="info-item">
                    <span class="label">Dispatch Date</span>
                    <span class="value">{{ fmtDate(params.dispatchDate) }}</span>
                </div>
                <div class="info-item">
                    <span class="label">Product</span>
                    <span class="value" :title="fmtFull(params.product)">{{
                        fmtMulti(params.product, 'products') }}</span>
                </div>
                <div class="info-item">
                    <span class="label">Operator</span>
                    <span class="value" :title="fmtFull(params.operator)">{{
                        fmtMulti(params.operator, 'operators') }}</span>
                </div>
                <div class="info-item">
                    <span class="label">Area</span>
                    <span class="value" :title="fmtFull(params.area)">{{
                        fmtMulti(params.area, 'areas') }}</span>
                </div>
                <div class="info-item">
                    <span class="label">Maximum DTE</span>
                    <span class="value">{{ params.maxDte != null ? params.maxDte + ' days' : '—' }}</span>
                </div>
                <div class="info-item">
                    <span class="label">Minimum Ullage</span>
                    <span class="value">{{ params.minUllage != null ? params.minUllage + ' L' : '—' }}</span>
                </div>
                <div class="info-item">
                    <span class="label">Maximum Fill</span>
                    <span class="value">{{ params.maxFillPct != null ? params.maxFillPct + '%' : '—' }}</span>
                </div>
                <div class="info-item">
                    <span class="label">Sites</span>
                    <span class="value">{{ sites.length }}</span>
                </div>
                <div class="info-item">
                    <span class="label">Total Fill Amount</span>
                    <span class="value">{{ fmtUllage(totalFill) }} L</span>
                </div>
                <div class="info-item">
                    <span class="label">Predicted Fill Amount
                        <span v-if="forecastDays" class="label-note">on dispatch date</span></span>
                    <span class="value">{{ fmtUllage(totalPredictedFill) }} L</span>
                </div>
            </div>

            <div v-if="isTotes && supplyRows.length" class="supply-review">
                <h4>Supply</h4>
                <table class="supply-table">
                    <thead>
                        <tr>
                            <th>Product</th>
                            <th class="num">Required (L)</th>
                            <th class="num">Tote Size (L)</th>
                            <th class="num">Totes Needed</th>
                            <th class="num">Totes Available</th>
                            <th class="num">Shortfall (L)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in supplyRows" :key="row.product">
                            <td>{{ row.product }}</td>
                            <td class="num">{{ fmtUllage(row.required) }}</td>
                            <td class="num">{{ row.toteSize ? fmtUllage(row.toteSize) : '—' }}</td>
                            <td class="num">{{ row.toteSize ? row.totesNeeded : '—' }}</td>
                            <td class="num">{{ row.totesAvailable != null ? row.totesAvailable : '—' }}</td>
                            <td class="num" :class="{ short: row.shortfall > 0 }">
                                {{ row.shortfall > 0 ? fmtUllage(row.shortfall) : '—' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p v-if="supplyShortfall > 0" class="supply-warn">
                    Supply is {{ fmtUllage(supplyShortfall) }} L short of what the selected
                    sites need.
                </p>
            </div>

            <div class="save-row">
                <DxButton icon="save" :text="saved ? 'Update Dispatch' : 'Save Dispatch'" type="default"
                    styling-mode="contained" :disabled="saving" @click="onSave" />               
            </div>
            <div class="save-row">
                <DxButton icon="map" text="View Map" styling-mode="outlined"
                    :disabled="!saved" @click="openMap" />
                <DxButton icon="exportpdf" text="Create PDF" styling-mode="outlined"
                    :disabled="!saved" @click="createPdf" />
                <DxButton icon="email" text="Email" styling-mode="outlined" :disabled="!saved"
                    @click="emailDispatch" />
            </div>
            <div v-if="saving" class="muted save-status">{{ saved ? 'Updating…' : 'Saving…' }}</div>
            <div v-else-if="saveError" class="save-status error">{{ saveError }}</div>
            <div v-else-if="saved" class="save-status success">
                Dispatch {{ updated ? 'updated' : 'saved' }} (ID: {{ savedId }}). You can now view the
                map, create a PDF or email it.
            </div>
        </div>

        <div class="nav-row">
            <DxButton icon="chevronleft" text="Previous" styling-mode="outlined" @click="$emit('previous')" />
            <DxButton icon="home" text="Done" styling-mode="outlined" :disabled="!saved"
                @click="$emit('done')" />
        </div>
    </div>
</template>

<script>
import { DxButton } from 'devextreme-vue/button';
import { normalizeProduct, NO_PRODUCT } from './delivery-planner';

// Longest list rendered in full on screen before it collapses to a count.
const LIST_LIMIT = 8;

export default {
    name: 'DispatchSummary',
    components: {
        DxButton,
    },
    emits: ['previous', 'done'],
    data() {
        return {
            saving: false,
            saved: false,
            updated: false,
            savedId: null,
            saveError: null,
        };
    },
    computed: {
        dispatch() {
            return this.$store.getters['dispatch/getCurrentDispatch'];
        },
        params() {
            return this.dispatch.parameters;
        },
        sites() {
            return this.dispatch.selectedSites || [];
        },
        // Days from today to the dispatch date — the horizon the predicted amounts
        // are forecast over.
        forecastDays() {
            return this.$store.getters['dispatch/getDispatchForecastDays'];
        },
        totalFill() {
            return this.sites.reduce((sum, s) => sum + (Number(s.fillAmount) || 0), 0);
        },
        // Uses the coords getter rather than the raw rows: it re-derives the
        // prediction against the current dispatch date, so editing the date after
        // picking sites is reflected here without re-selecting them.
        forecastSites() {
            return this.$store.getters['dispatch/getCurrentDispatchSitesWithCoords'];
        },
        // Recomputed from the live selection rather than read back from the saved
        // snapshot, so changing sites after the Supply step cannot leave stale
        // numbers on screen. Tote figures come from what the user entered.
        supplyRows() {
            const saved = this.$store.getters['dispatch/getCurrentDispatchSupply'] || {};
            const byProduct = saved.byProduct || {};
            return this.$store.getters['dispatch/getSupplySummary'].map((row) => {
                const entry = byProduct[row.product] || {};
                const toteSize = Number(entry.toteSize) || 0;
                const totesAvailable = Number(entry.totesAvailable) || 0;
                return {
                    ...row,
                    toteSize: entry.toteSize ?? null,
                    totesAvailable: entry.totesAvailable ?? null,
                    totesNeeded: toteSize > 0 ? Math.ceil(row.required / toteSize) : 0,
                    shortfall: toteSize > 0
                        ? Math.max(0, row.required - toteSize * totesAvailable)
                        : 0,
                };
            });
        },
        isTotes() {
            return this.$store.getters['dispatch/getDeliveryMethod'] === 'totes';
        },
        supplyShortfall() {
            return this.supplyRows.reduce((sum, r) => sum + r.shortfall, 0);
        },
        totalPredictedFill() {
            return this.forecastSites.reduce(
                (sum, s) => sum + (Number(s.predictedFillAmount) || 0),
                0
            );
        },
    },
    created() {
        // A dispatch loaded from Recent already exists, so treat it as saved:
        // the share actions (map / PDF / email) should be enabled immediately.
        if (this.dispatch.dispatchId) {
            this.saved = true;
            this.savedId = this.dispatch.dispatchId;
        }
    },
    methods: {
        // Product/Operator/Area are multi-select arrays; render them as a list, or
        // "All" when none are chosen. Past LIST_LIMIT entries the names stop fitting
        // the info cell, so only the count is shown — the full list stays available
        // as the cell's tooltip, and the PDF still spells every one of them out.
        fmtMulti(value, noun) {
            const list = this.toList(value);
            if (!list.length) return 'All';
            if (list.length <= LIST_LIMIT) return list.join(', ');
            return `${list.length} ${noun}`;
        },
        fmtFull(value) {
            const list = this.toList(value);
            return list.length ? list.join(', ') : 'All';
        },
        // Tolerates legacy single-string values.
        toList(value) {
            if (Array.isArray(value)) return value;
            return value ? [value] : [];
        },
        fmtDate(value) {
            if (!value) return '—';
            const d = new Date(value);
            return isNaN(d) ? value : d.toLocaleDateString();
        },
        fmtUllage(value) {
            return Math.round(Number(value) || 0).toLocaleString();
        },
        openMap() {
            // Cache the working dispatch so the new tab (fresh, empty store)
            // can rehydrate the selected sites, then open the map in a new tab
            // with the dispatch id in the URL.
            this.$store.dispatch('dispatch/cacheCurrentDispatch');
            const query = this.dispatch.dispatchId ? { id: this.dispatch.dispatchId } : {};
            const route = this.$router.resolve({ name: 'dispatch-map', query });
            window.open(route.href, '_blank');
        },
        async onSave() {
            this.saving = true;
            this.saveError = null;
            // A dispatch that already has an id (saved here, or loaded from
            // Recent) is updated in place; only a brand-new one is created. Both
            // stay available so edits made after the first save can be persisted.
            const isUpdate = this.dispatch.dispatchId != null && this.dispatch.dispatchId !== '';
            try {
                await this.$store.dispatch(
                    isUpdate ? 'dispatch/updateDispatch' : 'dispatch/saveDispatch'
                );
                // The action writes the id back into the store's current dispatch.
                this.savedId = this.dispatch.dispatchId || '—';
                this.saved = true;
                this.updated = isUpdate;
            } catch (error) {
                this.saveError =
                    error.message || `Failed to ${isUpdate ? 'update' : 'save'} dispatch.`;
            } finally {
                this.saving = false;
            }
        },
        async createPdf() {
            // Open the window synchronously so it isn't popup-blocked, then fill
            // it after the container data is loaded.
            const win = window.open('', '_blank');
            if (!win) return;
            win.document.write('<p style="font-family:Arial;margin:40px;">Generating dispatch…</p>');

            // The sites getter enriches the selected rows from the containers
            // module (same source the map uses), so make sure it's loaded.
            try {
                await this.$store.dispatch('containers/loadContainers');
            } catch (error) {
                console.warn('createPdf: failed to load coordinates.', error);
            }
            const sites = this.$store.getters['dispatch/getCurrentDispatchSitesWithCoords'];

            const fmtUllage = (v) => Math.round(Number(v) || 0).toLocaleString();
            // Property keys on the selected rows are lower-cased; tolerate the
            // other spellings seen elsewhere in the app.
            const downhole = (s) => {
                const v = s['downhole lsd'] != null ? s['downhole lsd']
                    : s.downholeLSD != null ? s.downholeLSD
                        : s['Downhole LSD'];
                return v != null && String(v).trim() !== '' ? v : '';
            };
            const totalFill = sites.reduce((sum, s) => sum + (Number(s.fillAmount) || 0), 0);
            const totalPredicted = sites.reduce(
                (sum, s) => sum + (Number(s.predictedFillAmount) || 0),
                0
            );
            const days = this.forecastDays;

            // Group by product. A truck compartment carries one product, so the
            // sheet is worked one product at a time — a flat list would have the
            // driver hunting for which rows belong to the load they're filling.
            // Sites keep their existing order within a group.
            const groups = [];
            const byProduct = new Map();
            for (const site of sites) {
                const key = normalizeProduct(site.product);
                let group = byProduct.get(key);
                if (!group) {
                    group = { product: key, sites: [], fill: 0, predicted: 0 };
                    byProduct.set(key, group);
                    groups.push(group);
                }
                group.sites.push(site);
                group.fill += Number(site.fillAmount) || 0;
                group.predicted += Number(site.predictedFillAmount) || 0;
            }
            // Alphabetical, with the catch-all bucket last.
            groups.sort((a, b) => {
                if (a.product === NO_PRODUCT) return 1;
                if (b.product === NO_PRODUCT) return -1;
                return a.product.localeCompare(b.product);
            });

            const esc = (v) =>
                String(v == null ? '' : v)
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;');

            const rowsFor = (list) =>
                list
                    .map(
                        (s) =>
                            `<tr><td>${s.operator || ''}</td>` +
                            `<td>${s.area || ''}</td>` +
                            `<td>${s.surface || s.alias || s.containerId}</td>` +
                            `<td>${downhole(s)}</td>` +
                            `<td class="num">${fmtUllage(s.fillAmount)}</td>` +
                            `<td class="num blank"></td>` +
                            `<td class="num">${fmtUllage(s.predictedFillAmount)}</td></tr>`
                    )
                    .join('');

            const tableFor = (list, footLabel, footFill, footPredicted) => `
                    <table>
                        <thead>
                            <tr>
                                <th>Operator</th><th>Area</th><th>Site</th>
                                <th>Downhole LSD</th>
                                <th class="num">Fill Amount (L)</th>
                                <th class="num">Actual Amount (L)</th>
                                <th class="num">Predicted Fill Amount (L)</th>
                            </tr>
                        </thead>
                        <tbody>${rowsFor(list)}</tbody>
                        <tfoot>
                            <tr>
                                <td colspan="4">${footLabel}</td>
                                <td class="num">${fmtUllage(footFill)}</td>
                                <td class="num blank"></td>
                                <td class="num">${fmtUllage(footPredicted)}</td>
                            </tr>
                        </tfoot>
                    </table>`;

            // One product is the old single-table sheet, unchanged. Only a genuine
            // product mix gets the breakdown table and per-product sections.
            const multi = groups.length > 1;

            const breakdown = !multi ? '' : `
                    <h2>Product Breakdown</h2>
                    <table class="breakdown">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th class="num">Sites</th>
                                <th class="num">Fill Amount (L)</th>
                                <th class="num">Predicted Fill Amount (L)</th>
                            </tr>
                        </thead>
                        <tbody>${groups
                            .map(
                                (g) =>
                                    `<tr><td>${esc(g.product)}</td>` +
                                    `<td class="num">${g.sites.length}</td>` +
                                    `<td class="num">${fmtUllage(g.fill)}</td>` +
                                    `<td class="num">${fmtUllage(g.predicted)}</td></tr>`
                            )
                            .join('')}</tbody>
                        <tfoot>
                            <tr>
                                <td>All products</td>
                                <td class="num">${sites.length}</td>
                                <td class="num">${fmtUllage(totalFill)}</td>
                                <td class="num">${fmtUllage(totalPredicted)}</td>
                            </tr>
                        </tfoot>
                    </table>`;

            const sections = !multi
                ? tableFor(sites, 'Total', totalFill, totalPredicted)
                : groups
                      .map(
                          (g) => `
                    <div class="product-section">
                        <h2>${esc(g.product)}
                            <span class="section-note">${g.sites.length} site${g.sites.length === 1 ? '' : 's'} ·
                                ${fmtUllage(g.fill)} L</span></h2>
                        ${tableFor(g.sites, `${esc(g.product)} subtotal`, g.fill, g.predicted)}
                    </div>`
                      )
                      .join('');

            win.document.open();
            win.document.write(`
                <html>
                <head>
                    <title>Dispatch - ${this.dispatch.name || ''}</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 40px; color: #222; }
                        h1 { font-size: 20px; }
                        table { border-collapse: collapse; width: 100%; margin-top: 16px; }
                        th, td { border: 1px solid #ccc; padding: 6px 10px; text-align: left; font-size: 13px; }
                        th { background: #f3f3f3; }
                        td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
                        /* Actual Amount is left empty for the delivery driver to write in. */
                        td.blank { min-width: 90px; background: #fff; }
                        tfoot td { font-weight: bold; background: #fafafa; }
                        .meta { margin: 6px 0; font-size: 14px; }
                        .note { margin: 12px 0 0; font-size: 12px; color: #555; font-style: italic; }
                        h2 { font-size: 15px; margin: 26px 0 0; padding-bottom: 4px;
                             border-bottom: 2px solid #444; }
                        .section-note { font-weight: normal; font-size: 12px; color: #555;
                                        margin-left: 8px; }
                        .breakdown { margin-bottom: 4px; }
                        /* Repeat the column headers when a product's table runs over a
                           page, and never split a single site's row. */
                        thead { display: table-header-group; }
                        tr { page-break-inside: avoid; }
                        .product-section { page-break-before: auto; }
                        h2 { page-break-after: avoid; }
                    </style>
                </head>
                <body>
                    <h1>Dispatch: ${this.dispatch.name || '(unnamed)'}</h1>
                    <div class="meta"><strong>Dispatch Date:</strong> ${this.fmtDate(this.params.dispatchDate)}</div>
                    <div class="meta"><strong>Product:</strong> ${this.fmtFull(this.params.product)}</div>
                    <div class="meta"><strong>Operator:</strong> ${this.fmtFull(this.params.operator)}</div>
                    <div class="meta"><strong>Area:</strong> ${this.fmtFull(this.params.area)}</div>
                    <div class="meta"><strong>Maximum DTE:</strong> ${this.params.maxDte != null ? this.params.maxDte + ' days' : '—'}</div>
                    <div class="meta"><strong>Minimum Ullage:</strong> ${this.params.minUllage != null ? this.params.minUllage + ' L' : '—'}</div>
                    <div class="meta"><strong>Maximum Fill:</strong> ${this.params.maxFillPct != null ? this.params.maxFillPct + '%' : '—'}</div>
                    <div class="meta"><strong>Sites:</strong> ${sites.length}</div>
                    <div class="meta"><strong>Total Fill Amount:</strong> ${fmtUllage(totalFill)} L</div>
                    <div class="meta"><strong>Total Predicted Fill Amount:</strong> ${fmtUllage(totalPredicted)} L</div>
                    <p class="note">Predicted Fill Amount is each site's requirement on the dispatch
                        date${days ? ` (${days} day${days === 1 ? '' : 's'} out)` : ''}, projected from its
                        current volume and Injection Avg.</p>
                    ${breakdown}
                    ${sections}
                </body>
                </html>
            `);
            win.document.close();
            win.focus();
            // win.print();
        },
        emailDispatch() {
            // Cache so the compose page can rehydrate if needed, then open the
            // rich-text email composer.
            this.$store.dispatch('dispatch/cacheCurrentDispatch');
            this.$router.push({ name: 'dispatch-email' });
        },
    },
};
</script>

<style scoped>
.dispatch-summary {
    max-width: 700px;
    margin: 0 auto;
}

h3 {
    margin-top: 0;
}

.muted {
    color: rgba(150, 150, 150, 0.9);
    font-size: 13px;
}

.info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin: 16px 0;
}

.info-item {
    display: flex;
    flex-direction: column;
}

.label {
    font-size: 11px;
    text-transform: uppercase;
    color: rgba(150, 150, 150, 0.9);
    letter-spacing: 0.04em;
}

.label-note {
    text-transform: none;
    letter-spacing: 0;
    font-style: italic;
    opacity: 0.75;
}

.value {
    font-size: 16px;
    margin-top: 2px;
}

.supply-review {
    margin: 24px 0 8px 0;
}

.supply-review h4 {
    margin: 0 0 10px 0;
    font-size: 14px;
    font-weight: 500;
}

.supply-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.supply-table th,
.supply-table td {
    border-bottom: 1px solid rgba(150, 150, 150, 0.2);
    padding: 6px 10px;
    text-align: left;
}

.supply-table th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(150, 150, 150, 0.9);
    font-weight: 400;
}

.supply-table .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
}

.supply-table .short {
    color: #ff5c69;
}

.supply-warn {
    margin-top: 10px;
    font-size: 13px;
    color: rgb(255, 140, 40);
}

.map-link {
    margin-top: 8px;
}

.save-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 12px;
}

.save-status {
    margin-top: 12px;
    font-size: 13px;
}

.save-status.success {
    color: #4caf50;
}

.save-status.error {
    color: #d9534f;
}

.nav-row {
    display: flex;
    justify-content: space-between;
    margin: 20px;
}
</style>
